<script lang="ts">
  import { viewport } from '../lib/stores/viewport';
  /**
   * PriorityTasksRail - Right panel showing high/medium priority tasks across all scheduled,
   * non-archived, non-done projects (not limited to the current timeline window).
   *
   * Data strategy:
   * - Subscribes to projectTasksCache (single subscription) for live task data
   * - Falls back to project.incompleteTasks preview for projects not yet in cache
   * - Edits in ProjectRail are immediately reflected here via shared cache
   *
   * Task mutations delegate to lib/taskApi.ts — no hand-rolled fetch/optimistic logic here.
   *
   * Features:
   * - Tasks grouped by project OR priority (toggle)
   * - When grouped by priority: Priority → Project → Tasks hierarchy with project badges
   * - TaskDetailPopover on task click (same as ProjectRail)
   * - Task action menu (⋯) with priority, won't do, delete
   * - Filter pills: All / High / Medium
   */
  import type { Project, Task, TaskPriority, TaskStatus } from '../lib/types';
  import { categoryIcons } from '../lib/utils';
  import { isWorkQueueOpen, workQueueTab, closeWorkQueue, openProjectRail } from '../lib/stores/ui';
  import WorkQueueTabs from './WorkQueueTabs.svelte';
  import {
    subscribeToAllCachedTasks,
    batchFetchProjectTasks,
  } from '../lib/stores/projectTasksCache';
  import {
    completeTask,
    updateTask,
    updateTaskPriority,
    markTaskWontDo,
    duplicateTask,
    deleteTask,
  } from '../lib/taskApi';
  import TaskDetailPopover from './TaskDetailPopover.svelte';
  import NowToggle from './NowToggle.svelte';
  import Icon from './Icon.svelte';
  import TaskCopyActionRow from './TaskCopyActionRow.svelte';

  interface Props {
    allProjects: Project[];
    backlogCount: number;
  }

  let { allProjects, backlogCount }: Props = $props();

  let isOpen = $derived($isWorkQueueOpen && $workQueueTab === 'priority');

  // Filter to show only high and medium priority tasks
  type PriorityFilter = 'all' | 'high' | 'medium';
  let priorityFilter = $state<PriorityFilter>('all');

  // Group mode: by project (default) or by priority
  type GroupMode = 'project' | 'priority';
  let groupMode = $state<GroupMode>('project');

  // Priority display
  const priorityBorderColors: Record<string, string> = {
    high: '#ef4444',
    medium: '#fb923c',
    low: '#60a5fa',
    none: '#d1d5db',
  };

  const priorityHoverBgs: Record<string, string> = {
    high: 'rgba(239, 68, 68, 0.08)',
    medium: 'rgba(251, 146, 60, 0.08)',
    low: 'rgba(96, 165, 250, 0.08)',
    none: 'rgba(209, 213, 219, 0.12)',
  };

  const STATUS_DOT_COLORS: Record<string, string> = {
    planned: '#3b82f6',
    active: '#d97706',
    almost: '#ea580c',
    done: '#16a34a',
  };

  const PRIORITY_GROUP_META: Record<string, { label: string; icon: string }> = {
    high: { label: 'High Priority', icon: '🔴' },
    medium: { label: 'Medium Priority', icon: '🟠' },
  };

  // Single subscription to the entire cache store (O(1) subscriptions instead of O(N))
  // When any project's tasks change, we get the full map and derive per-project data.
  let cachedTasksByProject = $state<Record<string, Task[]>>({});

  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: subscribes to all cached tasks when rail opens, batch-fetches project tasks, returns cleanup
  $effect(() => {
    if (!isOpen) return;

    // Get all scheduled project IDs
    const projectIds = allProjects.filter(isPriorityQueueProject).map((p) => p.id);

    // Ensure all project tasks are fetched into cache so mutations work correctly
    batchFetchProjectTasks(projectIds);

    // Single subscription to cache — extracts fetched tasks for all projects
    const unsub = subscribeToAllCachedTasks((allTasks) => {
      cachedTasksByProject = allTasks;
    });

    return unsub;
  });

  // Derive task data per project group
  interface ProjectGroup {
    projectId: string;
    projectTitle: string;
    projectCategory: string;
    projectStatus: string;
    incompleteTasks: Task[];
    completedTasks: Task[];
  }

  function isPriorityTask(priority: string): boolean {
    return priority === 'high' || priority === 'medium';
  }

  function isPriorityQueueProject(project: Project): boolean {
    return (
      project.schedulingState !== 'backlog' && project.status !== 'done' && !project.isArchived
    );
  }

  let projectGroups = $derived.by(() => {
    const groups: ProjectGroup[] = [];

    for (const project of allProjects) {
      if (!isPriorityQueueProject(project)) continue;

      const cachedTasks = cachedTasksByProject[project.id];

      let incomplete: Task[];
      let completed: Task[];

      if (cachedTasks && cachedTasks.length > 0) {
        // Use live cache data
        incomplete = cachedTasks
          .filter((t) => t.status === 'todo' && isPriorityTask(t.priority))
          .sort((a, b) => {
            const po: Record<string, number> = { high: 0, medium: 1 };
            return (po[a.priority] ?? 2) - (po[b.priority] ?? 2);
          });
        completed = cachedTasks
          .filter((t) => t.status !== 'todo' && isPriorityTask(t.priority))
          .sort((a, b) => {
            const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0;
            const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0;
            return bTime - aTime;
          });
      } else {
        // Fall back to preview (incomplete only, no completed from preview)
        incomplete = (project.incompleteTasks || [])
          .filter((t) => isPriorityTask(t.priority))
          .map((t) => ({
            id: t.id,
            title: t.title,
            priority: t.priority,
            notes: null,
            status: 'todo' as TaskStatus,
            completedAt: null,
            position: 0,
            isActive: false,
            projectId: project.id,
            createdAt: '',
            updatedAt: '',
          }));
        completed = [];
      }

      if (incomplete.length > 0) {
        groups.push({
          projectId: project.id,
          projectTitle: project.title,
          projectCategory: project.category,
          projectStatus: project.status,
          incompleteTasks: incomplete,
          completedTasks: completed,
        });
      }
    }

    // Sort groups alphabetically by project title for stable ordering
    groups.sort((a, b) => a.projectTitle.localeCompare(b.projectTitle));

    return groups;
  });

  // Apply priority filter
  let filteredGroups = $derived.by(() => {
    if (priorityFilter === 'all') return projectGroups;

    return projectGroups
      .map((g) => ({
        ...g,
        incompleteTasks: g.incompleteTasks.filter((t) => t.priority === priorityFilter),
        completedTasks: g.completedTasks.filter((t) => t.priority === priorityFilter),
      }))
      .filter((g) => g.incompleteTasks.length > 0 || g.completedTasks.length > 0);
  });

  // === Priority-grouped view ===
  // Groups tasks by priority level, then sub-groups by project within each priority
  interface PriorityProjectSubgroup {
    projectId: string;
    projectTitle: string;
    projectCategory: string;
    projectStatus: string;
    tasks: Task[];
    completedTasks: Task[];
  }

  interface PriorityGroupEntry {
    priority: string;
    label: string;
    icon: string;
    projectSubgroups: PriorityProjectSubgroup[];
    totalIncomplete: number;
  }

  let priorityGroupedView = $derived.by(() => {
    const priorityOrder = ['high', 'medium'] as const;
    const groups: PriorityGroupEntry[] = [];

    for (const priority of priorityOrder) {
      // Skip if filter is active and doesn't match
      if (priorityFilter !== 'all' && priorityFilter !== priority) continue;

      const projectSubgroups: PriorityProjectSubgroup[] = [];

      for (const pg of projectGroups) {
        const incomplete = pg.incompleteTasks.filter((t) => t.priority === priority);
        const completed = pg.completedTasks
          .filter((t) => t.priority === priority)
          .sort((a, b) => {
            const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0;
            const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0;
            return bTime - aTime;
          });

        if (incomplete.length > 0 || completed.length > 0) {
          projectSubgroups.push({
            projectId: pg.projectId,
            projectTitle: pg.projectTitle,
            projectCategory: pg.projectCategory,
            projectStatus: pg.projectStatus,
            tasks: incomplete,
            completedTasks: completed,
          });
        }
      }

      // Sort subgroups alphabetically
      projectSubgroups.sort((a, b) => a.projectTitle.localeCompare(b.projectTitle));

      const totalIncomplete = projectSubgroups.reduce((sum, sg) => sum + sg.tasks.length, 0);

      if (projectSubgroups.length > 0) {
        const meta = PRIORITY_GROUP_META[priority] || { label: priority, icon: '—' };
        groups.push({
          priority,
          label: meta.label,
          icon: meta.icon,
          projectSubgroups,
          totalIncomplete,
        });
      }
    }

    return groups;
  });

  // Counts for filter pills
  let allIncomplete = $derived(projectGroups.flatMap((g) => g.incompleteTasks));
  let highCount = $derived(allIncomplete.filter((t) => t.priority === 'high').length);
  let mediumCount = $derived(allIncomplete.filter((t) => t.priority === 'medium').length);
  let totalCount = $derived(allIncomplete.length);

  // Completed section toggle per group (works for both modes)
  let showCompletedMap = $state<Record<string, boolean>>({});

  function toggleCompleted(key: string) {
    showCompletedMap = { ...showCompletedMap, [key]: !showCompletedMap[key] };
  }

  // Task detail popover
  let popoverTask = $state<Task | null>(null);
  let popoverAnchorEl = $state<HTMLElement | null>(null);

  // Set when a row's ⋯ opens the task sheet on its menu (mobile), see openTaskMenu
  let popoverMenu = $state<'actions' | null>(null);

  function openTaskPopover(task: Task, anchorEl: HTMLElement, onMenu = false) {
    popoverMenu = onMenu ? 'actions' : null;
    if (popoverTask?.id === task.id) {
      closeTaskPopover();
      return;
    }
    popoverTask = task;
    popoverAnchorEl = anchorEl;
  }

  function closeTaskPopover() {
    popoverTask = null;
    popoverAnchorEl = null;
  }

  // Task action menu
  let openMenuId = $state<string | null>(null);

  function toggleMenu(taskId: string) {
    openMenuId = openMenuId === taskId ? null : taskId;
  }

  function closeMenus() {
    openMenuId = null;
  }

  // Mobile: a row's ⋯ opens the task sheet on its own ⋯ menu instead of the
  // floating menu, so both are the same menu (plans/mobile-v2.md §3.4)
  function openTaskMenu(task: Task, trigger: HTMLElement) {
    if (!$viewport.isMobile) {
      toggleMenu(task.id);
      return;
    }
    closeMenus();
    openTaskPopover(task, trigger, true);
  }

  // Get cached tasks for a project (for passing to taskApi helpers)
  function getProjectTasks(projectId: string): Task[] {
    return cachedTasksByProject[projectId] || [];
  }

  // === Task Mutations (delegated to taskApi.ts) ===

  async function handleToggleTask(task: Task) {
    const result = await completeTask(task.projectId, task.id, getProjectTasks(task.projectId));
    // Update popover if still viewing this task
    if (result.success && popoverTask?.id === task.id) {
      popoverTask = result.tasks.find((t) => t.id === task.id) ?? null;
    }
  }

  async function handlePopoverUpdate(task: Task, updates: Partial<Task>) {
    const result = await updateTask(
      task.projectId,
      task.id,
      updates,
      getProjectTasks(task.projectId),
    );
    if (result.success && popoverTask?.id === task.id) {
      popoverTask = result.tasks.find((t) => t.id === task.id) ?? null;
    }
  }

  async function handleSetPriority(task: Task, newPriority: TaskPriority) {
    if (task.priority === newPriority) return;
    closeMenus();
    const result = await updateTaskPriority(
      task.projectId,
      task.id,
      newPriority,
      getProjectTasks(task.projectId),
    );
    if (result.success && popoverTask?.id === task.id) {
      popoverTask = result.tasks.find((t) => t.id === task.id) ?? null;
    }
  }

  async function handleDuplicateTask(task: Task) {
    closeMenus();
    await duplicateTask(task.projectId, task.id, getProjectTasks(task.projectId));
  }

  async function handleMarkWontDo(task: Task, pendingUpdates?: Partial<Task>) {
    closeMenus();
    await markTaskWontDo(task.projectId, task.id, getProjectTasks(task.projectId), pendingUpdates);
  }

  function handleDeleteTask(task: Task) {
    closeMenus();
    deleteTask(task.projectId, task.id, getProjectTasks(task.projectId));
  }

  function handleOpenProject(projectId: string) {
    openProjectRail(projectId);
  }

  function handleRailClick() {
    if (openMenuId !== null) openMenuId = null;
  }
</script>

{#snippet taskItem(task: Task, isCompleted: boolean)}
  <div class="task-item" class:completed={isCompleted}>
    <button
      type="button"
      class="task-checkbox"
      class:checked={isCompleted}
      class:wont-do={task.status === 'wont_do'}
      style="--cb-border: {priorityBorderColors[task.priority]}; --cb-hover-bg: {priorityHoverBgs[
        task.priority
      ]};"
      onclick={() => handleToggleTask(task)}
      title={isCompleted
        ? task.status === 'wont_do'
          ? "Won't do — click to reopen"
          : 'Done — click to reopen'
        : 'Mark done'}
    >
      {#if task.status === 'wont_do'}
        <span class="checkbox-x">✕</span>
      {:else if isCompleted}
        <span class="checkbox-check">✓</span>
      {:else}
        <span class="checkbox-inner"></span>
      {/if}
    </button>
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <span
      class="task-title"
      class:wont-do-text={task.status === 'wont_do'}
      data-task-popover-id={task.id}
      onclick={(e) => openTaskPopover(task, e.currentTarget as HTMLElement)}
      role="button"
      tabindex="0">{task.title}</span
    >
    {#if task.notes}
      <span class="notes-indicator" title="Has notes">
        <svg viewBox="0 0 16 16" fill="currentColor">
          <path
            d="M4 1h8a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1zm1 3v1h6V4H5zm0 3v1h6V7H5zm0 3v1h4v-1H5z"
          />
        </svg>
      </span>
    {/if}
    <!-- Task actions dropdown -->
    <div class="task-actions" class:menu-open={openMenuId === task.id}>
      <button
        type="button"
        class="task-menu-btn hover-reveal"
        onclick={(e) => {
          e.stopPropagation();
          openTaskMenu(task, e.currentTarget as HTMLElement);
        }}
        title="Task actions"
        aria-label="Actions for {task.title}">⋯</button
      >
      <div class="task-menu" class:visible={openMenuId === task.id}>
        {#if !isCompleted}
          <div class="menu-label">Priority:</div>
          <div class="priority-row">
            <button
              type="button"
              class="priority-option"
              class:active={task.priority === 'none'}
              onclick={() => handleSetPriority(task, 'none')}
              title="No priority">—</button
            >
            <button
              type="button"
              class="priority-option"
              class:active={task.priority === 'low'}
              onclick={() => handleSetPriority(task, 'low')}
              title="Low">🔵</button
            >
            <button
              type="button"
              class="priority-option"
              class:active={task.priority === 'medium'}
              onclick={() => handleSetPriority(task, 'medium')}
              title="Medium">🟠</button
            >
            <button
              type="button"
              class="priority-option"
              class:active={task.priority === 'high'}
              onclick={() => handleSetPriority(task, 'high')}
              title="High">🔴</button
            >
          </div>
          <div class="menu-divider"></div>
          <NowToggle {task} />
        {/if}
        <button
          type="button"
          class="menu-item"
          onclick={() => {
            closeTaskPopover();
            handleDuplicateTask(task);
          }}><Icon name="duplicate" />Duplicate task</button
        >
        <TaskCopyActionRow
          {task}
          onDone={() => {
            closeTaskPopover();
            closeMenus();
          }}
        />
        {#if !isCompleted}
          <button
            type="button"
            class="menu-item"
            onclick={() => {
              closeTaskPopover();
              handleMarkWontDo(task);
            }}><Icon name="wont-do" />Won't do</button
          >
        {/if}
        <button
          type="button"
          class="menu-item menu-item-danger"
          onclick={() => {
            closeTaskPopover();
            handleDeleteTask(task);
          }}><Icon name="trash" />Delete</button
        >
      </div>
    </div>
  </div>
{/snippet}

{#if isOpen}
  <!-- svelte-ignore a11y_no_noninteractive_element_interactions, a11y_click_events_have_key_events -->
  <aside
    class="priority-rail"
    class:in-sheet={$viewport.isMobile}
    data-sheet-fill
    onclick={handleRailClick}
  >
    <!-- Header -->
    <header class="rail-header" data-sheet-drag-handle>
      <div class="header-title-row">
        <h2 class="header-title" data-sheet-title>🔥 Priority Tasks</h2>
        <button
          type="button"
          class="close-btn"
          onclick={closeWorkQueue}
          title="Close work queue"
          data-sheet-close
        >
          {#if $viewport.isMobile}<Icon name="close" />{:else}✕{/if}
        </button>
      </div>
      <div class="header-subtitle">High & medium priority tasks across scheduled projects</div>
      <WorkQueueTabs {backlogCount} />
    </header>

    <div class="priority-controls">
      <!-- Filter pills -->
      <div class="filter-pills">
        <button
          type="button"
          class="filter-pill"
          class:active={priorityFilter === 'all'}
          onclick={() => (priorityFilter = 'all')}
        >
          All <span class="pill-count">{totalCount}</span>
        </button>
        <button
          type="button"
          class="filter-pill"
          class:active={priorityFilter === 'high'}
          onclick={() => (priorityFilter = 'high')}
        >
          🔴 High <span class="pill-count">{highCount}</span>
        </button>
        <button
          type="button"
          class="filter-pill"
          class:active={priorityFilter === 'medium'}
          onclick={() => (priorityFilter = 'medium')}
        >
          🟠 Medium <span class="pill-count">{mediumCount}</span>
        </button>
      </div>

      <!-- Group mode toggle -->
      <div class="group-toggle">
        <span class="group-toggle-label">Group by:</span>
        <button
          type="button"
          class="group-toggle-btn"
          class:active={groupMode === 'project'}
          onclick={() => (groupMode = 'project')}
        >
          <Icon name="folder" size={10} />Project
        </button>
        <button
          type="button"
          class="group-toggle-btn"
          class:active={groupMode === 'priority'}
          onclick={() => (groupMode = 'priority')}
        >
          <Icon name="priority" size={10} />Priority
        </button>
      </div>
    </div>

    <!-- Task list -->
    <div class="rail-content custom-scrollbar">
      {#if totalCount === 0}
        <div class="empty-state">
          <span class="empty-icon">✨</span>
          <span class="empty-text">No priority tasks across your projects</span>
        </div>
      {:else if groupMode === 'project'}
        <!-- === GROUP BY PROJECT (default) === -->
        {#if filteredGroups.length === 0}
          <div class="empty-state">
            <span class="empty-icon">✨</span>
            <span class="empty-text">No {priorityFilter} priority tasks</span>
          </div>
        {:else}
          {#each filteredGroups as group (group.projectId)}
            <div class="project-group">
              <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
              <div
                class="project-group-header"
                onclick={() => handleOpenProject(group.projectId)}
                role="button"
                tabindex="0"
              >
                <span class="project-icon">{categoryIcons[group.projectCategory] || '📋'}</span>
                <span class="project-name">{group.projectTitle}</span>
                <span
                  class="project-status-dot"
                  style="background: {STATUS_DOT_COLORS[group.projectStatus] || '#9ca3af'};"
                ></span>
                <span class="project-task-count">{group.incompleteTasks.length}</span>
              </div>

              <!-- Incomplete tasks -->
              <div class="task-list">
                {#each group.incompleteTasks as task (task.id)}
                  {@render taskItem(task, false)}
                {/each}
              </div>

              <!-- Completed tasks (collapsible) -->
              {#if group.completedTasks.length > 0}
                <button
                  type="button"
                  class="completed-toggle"
                  onclick={() => toggleCompleted(group.projectId)}
                >
                  <span class="toggle-arrow" class:open={showCompletedMap[group.projectId]}>▶</span>
                  Done ({group.completedTasks.length})
                </button>

                {#if showCompletedMap[group.projectId]}
                  <div class="task-list completed-list">
                    {#each group.completedTasks as task (task.id)}
                      {@render taskItem(task, true)}
                    {/each}
                  </div>
                {/if}
              {/if}
            </div>
          {/each}
        {/if}
      {:else}
        <!-- === GROUP BY PRIORITY === -->
        {#if priorityGroupedView.length === 0}
          <div class="empty-state">
            <span class="empty-icon">✨</span>
            <span class="empty-text">No {priorityFilter} priority tasks</span>
          </div>
        {:else}
          {#each priorityGroupedView as pGroup (pGroup.priority)}
            <div class="priority-group">
              <!-- Priority level header -->
              <div class="priority-group-header">
                <span class="priority-group-icon">{pGroup.icon}</span>
                <span class="priority-group-label">{pGroup.label}</span>
                <span class="priority-group-count">{pGroup.totalIncomplete}</span>
              </div>

              <!-- Project subgroups within this priority -->
              {#each pGroup.projectSubgroups as subgroup (subgroup.projectId)}
                <div class="priority-project-subgroup">
                  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
                  <div
                    class="priority-project-header"
                    onclick={() => handleOpenProject(subgroup.projectId)}
                    role="button"
                    tabindex="0"
                  >
                    <span class="project-icon-sm"
                      >{categoryIcons[subgroup.projectCategory] || '📋'}</span
                    >
                    <span class="priority-project-name">{subgroup.projectTitle}</span>
                    <span
                      class="project-status-dot"
                      style="background: {STATUS_DOT_COLORS[subgroup.projectStatus] || '#9ca3af'};"
                    ></span>
                    <span class="project-task-count">{subgroup.tasks.length}</span>
                  </div>

                  <div class="task-list priority-task-list">
                    {#each subgroup.tasks as task (task.id)}
                      {@render taskItem(task, false)}
                    {/each}
                  </div>

                  <!-- Completed tasks for this project within this priority (collapsible) -->
                  {#if subgroup.completedTasks.length > 0}
                    <button
                      type="button"
                      class="completed-toggle priority-completed-toggle"
                      onclick={() =>
                        toggleCompleted(`priority-${pGroup.priority}-${subgroup.projectId}`)}
                    >
                      <span
                        class="toggle-arrow"
                        class:open={showCompletedMap[
                          `priority-${pGroup.priority}-${subgroup.projectId}`
                        ]}>▶</span
                      >
                      Done ({subgroup.completedTasks.length})
                    </button>

                    {#if showCompletedMap[`priority-${pGroup.priority}-${subgroup.projectId}`]}
                      <div class="task-list completed-list priority-completed-list">
                        {#each subgroup.completedTasks as task (task.id)}
                          {@render taskItem(task, true)}
                        {/each}
                      </div>
                    {/if}
                  {/if}
                </div>
              {/each}
            </div>
          {/each}
        {/if}
      {/if}
    </div>

    <!-- Footer -->
    <footer class="rail-footer">
      <span class="footer-hint"> Click project to expand </span>
    </footer>
  </aside>
{/if}

<!-- Task detail popover -->
<TaskDetailPopover
  task={popoverTask}
  anchorEl={popoverAnchorEl}
  initialMenu={popoverMenu}
  project={popoverTask ? (allProjects.find((p) => p.id === popoverTask?.projectId) ?? null) : null}
  onClose={closeTaskPopover}
  onUpdate={handlePopoverUpdate}
  onDelete={(task, pendingUpdates) => {
    if (pendingUpdates) handlePopoverUpdate(task, pendingUpdates);
    closeTaskPopover();
    handleDeleteTask(task);
  }}
  onMarkWontDo={(task, pendingUpdates) => {
    closeTaskPopover();
    handleMarkWontDo(task, pendingUpdates);
  }}
  onToggleComplete={(task, pendingUpdates) => {
    closeTaskPopover();
    if (pendingUpdates) handlePopoverUpdate(task, pendingUpdates);
    handleToggleTask(task);
  }}
/>

<style>
  .priority-rail {
    width: 18rem;
    background: #f9fafb;
    border-left: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    max-height: 100%;
  }

  .rail-header {
    padding: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
  }

  .header-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.375rem;
    margin-bottom: 0.25rem;
  }

  .header-title {
    font-size: 0.75rem;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.3;
  }

  .close-btn {
    width: 1.25rem;
    height: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s;
  }

  .close-btn:hover {
    color: #374151;
    background: rgba(0, 0, 0, 0.1);
  }

  .header-subtitle {
    font-size: 0.625rem;
    color: #6b7280;
    line-height: 1.4;
    margin-bottom: 0.5rem;
  }

  .priority-controls {
    padding: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
  }

  .filter-pills {
    display: flex;
    gap: 0.25rem;
    margin-bottom: 0.375rem;
  }

  .filter-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.125rem 0.375rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    font-family: inherit;
  }

  .filter-pill:hover {
    color: #374151;
    background: #e5e7eb;
  }

  .filter-pill.active {
    color: #1f2937;
    background: white;
    border-color: #d1d5db;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .pill-count {
    font-size: 0.5rem;
    color: #9ca3af;
    font-variant-numeric: tabular-nums;
  }

  .filter-pill.active .pill-count {
    color: #6b7280;
  }

  /* Group mode toggle */
  .group-toggle {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .group-toggle-label {
    font-size: 0.5625rem;
    color: #9ca3af;
    font-weight: 500;
    margin-right: 0.125rem;
  }

  .group-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.0625rem 0.3125rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #9ca3af;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.12s;
    font-family: inherit;
  }

  .group-toggle-btn:hover {
    color: #6b7280;
    background: #f3f4f6;
  }

  .group-toggle-btn.active {
    color: #2563eb;
    background: #eff6ff;
    border-color: #bfdbfe;
  }

  .rail-content {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem 0.75rem;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1.5rem 0;
    color: #9ca3af;
    gap: 0.25rem;
  }

  .empty-icon {
    font-size: 1.25rem;
  }

  .empty-text {
    font-size: 0.6875rem;
    text-align: center;
  }

  /* === Group by Project styles === */
  .project-group {
    margin-bottom: 0.625rem;
  }

  .project-group-header {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.25rem;
    margin-bottom: 0.125rem;
    border-bottom: 1px solid #f3f4f6;
    cursor: pointer;
    border-radius: 0.1875rem;
    transition: background 0.1s;
  }

  .project-group-header:hover {
    background: rgba(59, 130, 246, 0.06);
  }

  .project-icon {
    font-size: 0.625rem;
    flex-shrink: 0;
  }

  .project-name {
    font-size: 0.625rem;
    font-weight: 600;
    color: #374151;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .project-status-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .project-task-count {
    font-size: 0.5rem;
    color: #9ca3af;
    font-variant-numeric: tabular-nums;
  }

  /* === Group by Priority styles === */
  .priority-group {
    margin-bottom: 0.75rem;
  }

  .priority-group-header {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.25rem;
    margin-bottom: 0.25rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .priority-group-icon {
    font-size: 0.5625rem;
  }

  .priority-group-label {
    font-size: 0.625rem;
    font-weight: 700;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .priority-group-count {
    font-size: 0.5rem;
    color: #9ca3af;
    margin-left: auto;
    font-variant-numeric: tabular-nums;
  }

  .priority-project-subgroup {
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
  }

  .priority-project-header {
    display: flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.125rem 0.25rem;
    margin-bottom: 0.0625rem;
    cursor: pointer;
    border-radius: 0.1875rem;
    transition: background 0.1s;
  }

  .priority-project-header:hover {
    background: rgba(59, 130, 246, 0.06);
  }

  .project-icon-sm {
    font-size: 0.5625rem;
    flex-shrink: 0;
  }

  .priority-project-name {
    font-size: 0.5625rem;
    font-weight: 600;
    color: #6b7280;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .priority-task-list {
    margin-left: 0.25rem;
  }

  .priority-completed-toggle {
    margin-left: 0.25rem;
  }

  .priority-completed-list {
    margin-left: 0.25rem;
  }

  /* === Shared task styles === */
  .task-list {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .task-item {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.25rem 0.1875rem 0.75rem;
    border-radius: 0.25rem;
    transition: background 0.1s;
  }

  .task-item:hover {
    background: rgba(0, 0, 0, 0.03);
  }

  .task-item.completed {
    opacity: 0.6;
  }

  .task-checkbox {
    width: 0.875rem;
    height: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid var(--cb-border, #d1d5db);
    border-radius: 0.1875rem;
    background: white;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s;
    padding: 0;
  }

  .task-checkbox:hover {
    background: var(--cb-hover-bg, rgba(209, 213, 219, 0.12));
  }

  .task-checkbox.checked {
    background: #3b82f6;
    border-color: #3b82f6;
  }

  .task-checkbox.wont-do {
    background: #9ca3af;
    border-color: #9ca3af;
  }

  .checkbox-inner {
    width: 0;
    height: 0;
  }

  .checkbox-check,
  .checkbox-x {
    font-size: 0.5625rem;
    color: white;
    font-weight: 700;
    line-height: 1;
  }

  .task-title {
    flex: 1;
    font-size: 0.6875rem;
    color: #1f2937;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.125rem;
    transition: background 0.1s;
  }

  .task-title:hover {
    background: rgba(59, 130, 246, 0.08);
  }

  .task-title.wont-do-text {
    text-decoration: line-through;
    color: #9ca3af;
  }

  .notes-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 0.75rem;
    height: 0.75rem;
    color: #9ca3af;
    flex-shrink: 0;
    margin-right: 0.125rem;
  }

  .notes-indicator svg {
    width: 100%;
    height: 100%;
  }

  /* Task actions dropdown */
  .task-actions {
    position: relative;
  }

  .task-menu-btn {
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6875rem;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.125rem;
    cursor: pointer;
    opacity: 0.35;
    transition: all 0.15s;
  }

  .task-item:hover .task-menu-btn {
    opacity: 1;
    color: #6b7280;
  }

  .task-menu-btn:hover {
    color: #374151;
    background: rgba(0, 0, 0, 0.08);
  }

  .menu-open .task-menu-btn {
    opacity: 1;
  }

  .task-menu {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.125rem;
    min-width: 10rem;
    max-height: 16rem;
    overflow-y: auto;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 60;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.1s,
      visibility 0.1s;
  }

  .task-menu.visible {
    opacity: 1;
    visibility: visible;
  }

  .menu-label {
    padding: 0.25rem 0.5rem;
    font-size: 0.5625rem;
    color: #9ca3af;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .priority-row {
    display: flex;
    gap: 0.125rem;
    padding: 0.125rem 0.5rem 0.25rem;
  }

  .priority-option {
    width: 1.5rem;
    height: 1.375rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.1s;
    padding: 0;
  }

  .priority-option:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  .priority-option.active {
    background: #eff6ff;
    border-color: #3b82f6;
  }

  .menu-divider {
    height: 1px;
    background: #e5e7eb;
    margin: 0.125rem 0;
  }

  .menu-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    padding: 0.375rem 0.5rem;
    font-size: 0.6875rem;
    color: #374151;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
  }

  .menu-item:hover {
    background: #f3f4f6;
  }

  .menu-item-danger {
    color: #dc2626;
  }

  .menu-item-danger:hover {
    background: #fef2f2;
  }

  .menu-item :global(.icon) {
    color: #6b7280;
  }

  .menu-item-danger :global(.icon) {
    color: inherit;
  }

  /* Completed toggle */
  .completed-toggle {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.5rem 0.1875rem 0.75rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #9ca3af;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.15s;
    font-family: inherit;
    margin-top: 0.125rem;
  }

  .completed-toggle:hover {
    color: #6b7280;
  }

  .toggle-arrow {
    font-size: 0.4375rem;
    transition: transform 0.15s;
    display: inline-block;
  }

  .toggle-arrow.open {
    transform: rotate(90deg);
  }

  .completed-list {
    margin-top: 0.125rem;
  }

  .rail-footer {
    padding: 0.375rem 0.75rem;
    border-top: 1px solid #e5e7eb;
    background: white;
  }

  .footer-hint {
    font-size: 0.5625rem;
    color: #9ca3af;
  }

  /* Mobile: inside the Dashboard's rail bottom sheet. */
  .priority-rail.in-sheet {
    flex: 1 1 auto;
    min-height: 0;
    border-left: none;
  }

  .priority-rail.in-sheet .rail-header {
    background: white;
  }

  /* ⋯ always visible (no hover) with a 44px hit area */
  .priority-rail.in-sheet .task-menu-btn {
    width: var(--mobile-tap);
    height: var(--mobile-tap);
    margin: -0.25rem -0.5rem -0.25rem 0;
    font-size: 1rem;
    color: #6b7280;
    opacity: 1;
  }

  .priority-rail.in-sheet .rail-content {
    min-height: 0;
    overscroll-behavior: contain;
  }
</style>
